export interface UserMinimal {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface AreaTagSetMinimal {
  id: number;
  name: string;
}

export interface LocationMinimal {
  id: number;
  name: string;
  layout_id?: number;
  layout_name?: string | null;
  area_tag_sets?: AreaTagSetMinimal[];
}

export const formatLocationLabel = (location?: LocationMinimal | null): string => {
  if (!location) return '—';
  return location.layout_name ? `${location.layout_name} - ${location.name}` : location.name;
};

export interface ObservationType {
  id: string;
  name: string;
}

export interface Trade {
  id: string;
  name: string;
}

export type ObservationPriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type ObservationStatus = 'OPEN' | 'COMPLETED' | 'OVERDUE' | 'CLOSED';

export interface HotspotDetail {
  id: number;
  pitch: number;
  yaw: number;
  hfov: number;
  photo_id: number;
}

export interface ObservationAttachment {
  id: string;
  observation: string;
  file: string;
  file_url: string | null;
  uploaded_by: UserMinimal | null;
  created_at: string;
}

export interface Observation {
  id: string;
  number: number;
  title: string;
  project: number;
  status: ObservationStatus;
  priority: ObservationPriority;
  assignee: UserMinimal | null;
  created_by: UserMinimal | null;
  location: LocationMinimal | null;
  observation_type: ObservationType | null;
  due_date: string | null;
  notification_date: string | null;
  spec_section: string;
  trade: Trade | null;
  distribution_list: UserMinimal[];
  is_private: boolean;
  description: string;
  hazard: string;
  contributing_behavior: string;
  contributing_condition: string;
  attachments: ObservationAttachment[];
  hotspot_detail: HotspotDetail | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface ObservationSummary {
  id: string;
  number: number;
  title: string;
  status: ObservationStatus;
  priority: ObservationPriority;
  assignee: UserMinimal | null;
  created_by: UserMinimal | null;
  due_date: string | null;
  location: LocationMinimal | null;
  observation_type: ObservationType | null;
  is_private: boolean;
  created_at: string;
  hotspot_id: number | null;
  can_edit: boolean;
  can_delete: boolean;
}

export interface CreateObservationData {
  title: string;
  project: number;
  status?: ObservationStatus;
  priority?: ObservationPriority;
  assignee?: number | null;
  location?: number | null;
  observation_type?: string | null;
  due_date?: string | null;
  notification_date?: string | null;
  spec_section?: string;
  trade?: string | null;
  is_private?: boolean;
  description?: string;
  hazard?: string;
  contributing_behavior?: string;
  contributing_condition?: string;
  distribution_list?: number[];
  hotspot?: number | null;
}

export type UpdateObservationData = Partial<CreateObservationData>;

export interface ObservationFilters {
  status?: ObservationStatus;
  priority?: ObservationPriority;
  assignee?: number;
  search?: string;
  hotspot_id?: number;
  photo_id?: number;
  area_tag_id?: number;
  area_tag_set_id?: number;
}

export interface ObservationStatusSummaryEntry {
  status: ObservationStatus;
  count: number;
}

export type ObservationSortKey =
  | 'number'
  | 'status'
  | 'priority'
  | 'assignee'
  | 'due_date'
  | 'created_at';

export interface ObservationSortState {
  key: ObservationSortKey;
  direction: 'asc' | 'desc';
}

export const PRIORITY_LABELS: Record<ObservationPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const STATUS_LABELS: Record<ObservationStatus, string> = {
  OPEN: 'Open',
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
  CLOSED: 'Closed',
};
